import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import { validateAndSanitizeText } from '../utils/sanitization';

const TodoListComponent = () => {
  const { todoEntries, saveTodoEntry, markTodoCompleted, plantGroups } = useAuth();
  const [isAddingTodo, setIsAddingTodo] = useState(false);
  const [newTodoText, setNewTodoText] = useState('');
  const [filterPlantGroup, setFilterPlantGroup] = useState('all');

  const handleAddTodo = async (e) => {
    e.preventDefault();
    if (newTodoText.trim()) {
      // Sanitize input before saving
      const todoEntry = {
        id: Date.now().toString(),
        text: validateAndSanitizeText(newTodoText.trim(), 2000),
        completed: false,
        createdAt: new Date().toISOString()
      };

      await saveTodoEntry(todoEntry);
      setNewTodoText('');
      setIsAddingTodo(false);
    }
  };

  const handleToggleTodo = async (todoId) => {
    await markTodoCompleted(todoId);
  };

  // Apply plant group filter
  const filteredTodos = filterPlantGroup === 'all'
    ? todoEntries
    : todoEntries.filter(todo => todo.plantGroupId === filterPlantGroup);

  const pendingTodos = filteredTodos.filter(todo => !todo.completed);
  const completedTodos = filteredTodos
    .filter(todo => todo.completed)
    .sort((a, b) => new Date(b.completedAt || b.createdAt) - new Date(a.completedAt || a.createdAt))
    .slice(0, 10);

  return (
    <div className="todo-container">
      <div className="todo-header">
        <h2>📝 Todo List</h2>
        <button
          className="todo-add-button"
          onClick={() => setIsAddingTodo(!isAddingTodo)}
        >
          {isAddingTodo ? '✖️ Cancel' : '➕ Add Todo'}
        </button>
      </div>

      {/* Plant Group Filter */}
      {plantGroups.length > 0 && (
        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="plantGroupFilter" style={{ marginRight: '10px', fontWeight: 'bold' }}>
            Filter by Plant Group:
          </label>
          <select
            id="plantGroupFilter"
            value={filterPlantGroup}
            onChange={(e) => setFilterPlantGroup(e.target.value)}
            style={{ padding: '5px 10px', fontSize: '14px', borderRadius: '4px' }}
          >
            <option value="all">All Todos</option>
            <option value="null">No Plant Group</option>
            {plantGroups.map(group => (
              <option key={group.id} value={group.id}>
                {group.plantName}
              </option>
            ))}
          </select>
        </div>
      )}

      {isAddingTodo && (
        <form className="todo-form" onSubmit={handleAddTodo}>
          <div className="todo-form-group">
            <label htmlFor="todoText">New Todo:</label>
            <input
              id="todoText"
              type="text"
              value={newTodoText}
              onChange={(e) => setNewTodoText(e.target.value)}
              placeholder="Enter your todo item..."
              autoFocus
            />
          </div>
          <div className="todo-form-actions">
            <button type="submit" className="todo-save-button">
              💾 Add Todo
            </button>
          </div>
        </form>
      )}

      <div className="todo-sections">
        <div className="todo-section">
          <h3 className="todo-section-title">
            📋 Pending ({pendingTodos.length})
          </h3>
          {pendingTodos.length === 0 ? (
            <div className="todo-empty">
              🎉 No pending todos! You're all caught up.
            </div>
          ) : (
            <div className="todo-entries">
              {pendingTodos.map(todo => (
                <div key={todo.id} className="todo-entry pending">
                  <div className="todo-entry-content">
                    <button
                      className="todo-checkbox"
                      onClick={() => handleToggleTodo(todo.id)}
                      title="Mark as completed"
                    >
                      ⬜
                    </button>
                    <div style={{ flex: 1 }}>
                      <span className="todo-text">{todo.text}</span>
                      {todo.plantGroupName && (
                        <div style={{ fontSize: '11px', color: '#28a745', marginTop: '3px' }}>
                          🌱 {todo.plantGroupName}
                        </div>
                      )}
                      {todo.dueDate && (
                        <div style={{ fontSize: '11px', color: '#dc3545', marginTop: '3px' }}>
                          📅 Due: {new Date(todo.dueDate).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="todo-entry-date">
                    {new Date(todo.createdAt).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {completedTodos.length > 0 && (
          <div className="todo-section">
            <h3 className="todo-section-title">
              ✅ Recently Completed ({completedTodos.length})
            </h3>
            <div className="todo-entries">
              {completedTodos.map(todo => (
                <div key={todo.id} className="todo-entry completed">
                  <div className="todo-entry-content">
                    <button
                      className="todo-checkbox completed"
                      onClick={() => handleToggleTodo(todo.id)}
                      title="Mark as pending"
                    >
                      ✅
                    </button>
                    <div style={{ flex: 1 }}>
                      <span className="todo-text completed">{todo.text}</span>
                      {todo.plantGroupName && (
                        <div style={{ fontSize: '11px', color: '#28a745', marginTop: '3px' }}>
                          🌱 {todo.plantGroupName}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="todo-entry-date">
                    Completed: {new Date(todo.completedAt || todo.createdAt).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TodoListComponent;